import { useRef } from "react";
import { ExternalLink } from "lucide-react";
import clsx from "clsx";

import { Tooltip } from "./Tooltip";
import { TruncatedText } from "./TruncatedText";

interface LinkedRecordValueProps {
  /** Text to show — e.g. the referenced contract's number, title, or id. */
  label: string;
  /** Opens the record's Quick View. Omit to render the label as plain text. */
  onOpenQuickView?: () => void;
  /** Route to the record's own detail page, for the new-tab icon. */
  href?: string;
  quickViewTitle?: string;
  newTabTitle?: string;
  className?: string;
  /** Hover text when there is nothing to open (no Quick View, no page) — e.g. why. */
  plainTitle?: string;
}

/**
 * A value that points at another record, shown as a link that opens it in
 * place (a Quick View) with a second control that opens the record's own page
 * in a new tab.
 *
 * Both stop propagation: this renders inside cards and rows that are
 * themselves clickable, and a click meant for the referenced record must not
 * also open the one it sits on.
 */
export function LinkedRecordValue({
  label,
  onOpenQuickView,
  href,
  quickViewTitle,
  newTabTitle,
  className,
  plainTitle,
}: LinkedRecordValueProps) {
  const labelRef = useRef<HTMLButtonElement | null>(null);

  if (!onOpenQuickView && !href) {
    return (
      <span className={className} title={plainTitle}>
        {label}
      </span>
    );
  }

  return (
    // min-w-0/max-w-full on both this row and the label's own flex item:
    // without them a flex item refuses to shrink below its content, so the
    // `truncate` below never engaged and a long contract number ran out past
    // the tile and under the new-tab icon.
    <span
      className={clsx(
        "inline-flex min-w-0 max-w-full items-center gap-1.5",
        className,
      )}
    >
      {onOpenQuickView ? (
        <Tooltip
          className="min-w-0"
          getLabel={() => {
            const el = labelRef.current;
            const clipped = !!el && el.scrollWidth > el.clientWidth;
            // When the value is clipped the full text is the thing you can't
            // otherwise get at, so it wins the bubble; the action hint is
            // only worth showing when the value is already fully readable.
            return clipped ? label : quickViewTitle;
          }}
        >
          <button
            ref={labelRef}
            type="button"
            aria-label={quickViewTitle}
            onClick={(e) => {
              e.stopPropagation();
              onOpenQuickView();
            }}
            className="
            truncate text-left font-semibold
            text-[rgb(var(--text-accent))]
            hover:underline
            focus:outline-none focus-visible:ring-2
            focus-visible:ring-[rgb(var(--text-accent))] rounded
          "
          >
            {label}
          </button>
        </Tooltip>
      ) : (
        <TruncatedText className="min-w-0 font-semibold" text={label} />
      )}

      {href && (
        <Tooltip label={newTabTitle}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={newTabTitle}
            onClick={(e) => e.stopPropagation()}
            // The icon stays 13px so the row doesn't reflow; the ::before
            // pads the hit area to 25px (WCAG 2.5.8's 24px minimum). Its 6px
            // reach on the left fits exactly into the row's gap-1.5, so it
            // never steals clicks from the label beside it.
            className="
            relative shrink-0 text-[rgb(var(--text-muted))]
            before:absolute before:-inset-[6px] before:content-['']
            hover:text-[rgb(var(--text-accent))] transition-colors
            focus:outline-none focus-visible:ring-2
            focus-visible:ring-[rgb(var(--text-accent))] rounded
          "
          >
            <ExternalLink size={13} />
          </a>
        </Tooltip>
      )}
    </span>
  );
}
